import { ReactElement } from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { FormattedMessage, useIntl, defineMessages } from 'react-intl'
import styled from 'styled-components'
import { CircleCloseIcon } from 'components/Icons/CircleCloseIcon'
import { DragIndicatorIcon } from 'components/Icons/DragIndicatorIcon'
import { PlaceAutocomplete } from 'features/Places/components/PlaceAutocomplete'
import { GTPlace, GTPlaceSuggestion } from 'gql/types.generated'
import { FormikErrors, FormikTouched, getIn } from 'formik'

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const AddStopContainer = styled.div`
  width: 85%;
  margin-left: 32px;
`
const messages = defineMessages({
  originPlaceholder: {
    id: 'routes.new.form.origin.placeholder',
    defaultMessage: 'Select origin'
  },
  destinationPlaceholder: {
    id: 'routes.new.form.destination.placeholder',
    defaultMessage: 'Select destination'
  },
  stopsPlaceholder: {
    id: 'routes.new.form.stops.placeholder',
    defaultMessage: 'Select stops (Optional)'
  },
  addPlaceholder: {
    id: 'routes.new.form.add.placeholder',
    defaultMessage: 'Add'
  }
})

const StopsWrapper = styled.div`
  > * {
    margin-bottom: 24px;
  }

  margin-bottom: -24px;
`

const StopWrapper = styled.div<{ isReplacingPlaces?: boolean; editDisabled?: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ isReplacingPlaces, editDisabled }): string =>
    isReplacingPlaces === true || editDisabled ? '1fr' : 'min-content 1fr min-content'};
  align-items: center;
  gap: 8px;
`
const LabelWrapper = styled.div<{ isReplacingPlaces?: boolean; showLabel?: boolean }>`
  display: ${({ showLabel }): string => (showLabel === true ? 'block' : 'none')};
  grid-column-start: ${({ isReplacingPlaces: isDragDisabled }): string => (isDragDisabled === true ? '1' : '2')};
  grid-column-end: ${({ isReplacingPlaces: isDragDisabled }): string => (isDragDisabled === true ? 'auto' : '4')};
`
const StyledCircleCloseIcon = styled(CircleCloseIcon)`
  cursor: pointer;
  width: 22px;
  height: 22px;
  fill: #b0b0b0;

  :hover {
    opacity: 0.6;
  }
`

const PlaceAutocompleteWrapper = styled.div`
  flex: 1;
`

const getStopLabel = (stopIndex: number, stops = [] as GTPlaceSuggestion[]): ReactElement => {
  if (stopIndex === 0) {
    return <FormattedMessage id="routes.edit.form.origin.label" defaultMessage="Origin" />
  } else if (stopIndex === stops.length - 1) {
    return <FormattedMessage id="routes.edit.form.destination.label" defaultMessage="Destination" />
  }

  return (
    <FormattedMessage
      id="routes.edit.form.stop.label"
      defaultMessage="Stop {stopIndex}"
      values={{
        stopIndex
      }}
    />
  )
}

export interface RouteStopsFields {
  stops: GTPlace[]
}

interface IRouteStopsProps {
  stops: GTPlaceSuggestion[]
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void
  touched: FormikTouched<RouteStopsFields>
  errors: FormikErrors<RouteStopsFields>
  isReplacingPlaces?: boolean
  handleDragEnd: (result: TSFixMe) => void
  checkCanEditStop?: (stop: GTPlaceSuggestion) => boolean
  onDelete: (stopIndex: number) => void
  onChange: (value: GTPlaceSuggestion, stopIndex: number) => void
  businessSiteId?: string
  businessClientId?: string
  companyId?: string
  showLabels?: boolean
  onAddStop?: (stop: GTPlaceSuggestion) => void
  showAddStop?: boolean
  disabled?: boolean
  vertical: string
}

export const RouteStops = ({
  stops,
  handleBlur,
  touched,
  errors,
  isReplacingPlaces,
  handleDragEnd,
  checkCanEditStop = (): boolean => true,
  onDelete,
  onChange,
  businessSiteId,
  businessClientId,
  companyId,
  onAddStop,
  showAddStop,
  showLabels = true,
  disabled = false,
  vertical
}: IRouteStopsProps): ReactElement => {
  const intl = useIntl()
  return (
    <Container>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="droppable" direction="vertical">
          {(provided): ReactElement => {
            return (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                <StopsWrapper>
                  {stops.map((stop, index: number): ReactElement | null => {
                    const canEditStop = checkCanEditStop?.(stop)

                    return (
                      <StopItem
                        showLabel={showLabels}
                        key={index}
                        stop={stop}
                        stopIndex={index}
                        stops={stops}
                        handleBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                        isReplacingPlaces={isReplacingPlaces}
                        canEditStop={canEditStop}
                        onDelete={onDelete}
                        onChange={onChange}
                        businessSiteId={businessSiteId}
                        businessClientId={businessClientId}
                        companyId={companyId}
                        disabled={disabled}
                        vertical={vertical}
                      />
                    )
                  })}
                  {showAddStop && (
                    <AddStopContainer>
                      <PlaceAutocomplete
                        onChange={(stop: GTPlaceSuggestion): void => {
                          if (!stop.id) return
                          onAddStop?.(stop)
                        }}
                        filteredValues={stops}
                        placeholder={intl.formatMessage(messages.addPlaceholder)}
                        disabled={isReplacingPlaces === false && !showAddStop}
                        businessSiteId={businessSiteId}
                        businessClientId={businessClientId}
                        companyId={companyId}
                        includeWithNoSites
                        vertical={vertical}
                      />
                    </AddStopContainer>
                  )}
                </StopsWrapper>
                {provided.placeholder}
              </div>
            )
          }}
        </Droppable>
      </DragDropContext>
    </Container>
  )
}

interface IStopItemProps extends Omit<IRouteStopsProps, 'handleDragEnd' | 'onAddStop'> {
  stop: GTPlaceSuggestion
  stopIndex: number
  canEditStop: boolean
  showLabel?: boolean
}

const StopItem = ({
  stop,
  stopIndex,
  stops,
  handleBlur,
  touched,
  errors,
  isReplacingPlaces,
  canEditStop,
  onDelete,
  onChange,
  showLabel,
  businessSiteId,
  businessClientId,
  companyId,
  disabled = false,
  vertical
}: IStopItemProps): ReactElement => {
  const intl = useIntl()

  return (
    <Draggable draggableId={String(stopIndex)} index={stopIndex}>
      {(provided): ReactElement => {
        return (
          <StopWrapper
            ref={provided.innerRef}
            {...provided.draggableProps}
            isReplacingPlaces={isReplacingPlaces}
            editDisabled={disabled}
          >
            <LabelWrapper isReplacingPlaces={isReplacingPlaces} showLabel={showLabel}>
              {getStopLabel(stopIndex, stops)}
            </LabelWrapper>
            {!isReplacingPlaces && !disabled && (
              <div {...provided.dragHandleProps}>
                <DragIndicatorIcon />
              </div>
            )}
            <PlaceAutocompleteWrapper>
              <PlaceAutocomplete
                id={`stops[${stopIndex}].id`}
                onChange={(stop: GTPlaceSuggestion): void => {
                  onChange(stop, stopIndex)
                }}
                filteredValues={stops}
                onBlur={handleBlur}
                value={stop.name}
                placeholder={intl.formatMessage(messages.originPlaceholder)}
                touched={touched.stops && touched.stops[stopIndex] && touched.stops[stopIndex].id}
                errors={getIn(errors, `stops[${stopIndex}].id`)}
                disabled={disabled || (isReplacingPlaces === false && !canEditStop)}
                businessSiteId={businessSiteId}
                businessClientId={businessClientId}
                companyId={companyId}
                includeWithNoSites
                vertical={vertical}
              />
            </PlaceAutocompleteWrapper>
            {!isReplacingPlaces && !disabled && (
              <div onClick={(): void => onDelete(stopIndex)}>
                <StyledCircleCloseIcon />
              </div>
            )}
          </StopWrapper>
        )
      }}
    </Draggable>
  )
}
